import React from 'react';
import clsx from 'clsx';
import styles from './Radio.module.css';

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  error?: string | React.ReactNode;
  wrapperClassName?: string;
  dataTestId?: string;
}

function Radio({
  ref,
  label,
  error,
  required,
  className,
  wrapperClassName,
  checked,
  disabled,
  id,
  dataTestId,
  ...props
}: RadioProps) {
  // Generate ID for label association if not provided
  const generatedId = React.useId();
  const radioId = id || `radio-${generatedId}`;

  return (
    <div className={clsx(styles.radioContainer, wrapperClassName)}>
      <label
        htmlFor={radioId}
        className={clsx(
          styles.radioWrapper,
          disabled && styles.radioWrapperDisabled,
          error && styles.radioWrapperError
        )}
        data-test-id={dataTestId ? `${dataTestId}-label` : undefined}
      >
        <input
          id={radioId}
          type="radio"
          checked={checked}
          disabled={disabled}
          className={className}
          aria-describedby={error ? `${radioId}-error` : undefined}
          data-test-id={dataTestId ? `${dataTestId}-input` : undefined}
          {...props}
          ref={ref}
        />

        <span className={styles.radioCustom}>{checked && <span className={styles.radioDot} />}</span>

        {label && (
          <span className={styles.radioLabel}>
            {label}
            {required && <span className={styles.required}>*</span>}
          </span>
        )}
      </label>

      {error && (
        <div id={`${radioId}-error`} className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default Radio;
